/**
 * MetricGauge — horizontal bar gauge for percentage metrics.
 *
 * Renders a labelled progress bar that changes colour based on configurable
 * warn/crit thresholds (BR-U5-01 through BR-U5-03):
 *   value < warnThreshold  → green  (OK)
 *   value < critThreshold  → amber  (WARN)
 *   value >= critThreshold → red    (CRIT)
 *
 * Uses role="meter" + aria-valuenow/min/max for screen reader accessibility (NFR-A11Y-07).
 */
import styles from './MetricGauge.module.css';

export type GaugeColour = 'green' | 'amber' | 'red';

/**
 * Derives the gauge colour from a metric value and thresholds.
 * Pure function — suitable for property-based testing (PBT-03).
 *
 * @param value       - The metric value (0–100)
 * @param warnThreshold - Value at which colour transitions from green to amber
 * @param critThreshold - Value at which colour transitions from amber to red
 * @returns           - Always one of: 'green' | 'amber' | 'red'
 */
export function deriveGaugeColour(
  value: number,
  warnThreshold: number,
  critThreshold: number,
): GaugeColour {
  if (value >= critThreshold) return 'red';
  if (value >= warnThreshold) return 'amber';
  return 'green';
}

export interface MetricGaugeProps {
  /** Display label, e.g. "CPU", "Memory", "Heap" */
  label: string;
  /** The current metric value, 0–100 */
  value: number | null;
  /** Threshold at which the gauge turns amber (default: 70) */
  warnThreshold?: number;
  /** Threshold at which the gauge turns red (default: 90) */
  critThreshold?: number;
  /** Unit to display after the value, e.g. "%" (default: "%") */
  unit?: string;
}

export function MetricGauge({
  label,
  value,
  warnThreshold = 70,
  critThreshold = 90,
  unit = '%',
}: MetricGaugeProps) {
  if (value === null || value === undefined) {
    return (
      <div className={styles.container}>
        <span className={styles.label}>{label}</span>
        <span className={styles.noData}>N/A</span>
      </div>
    );
  }

  const clamped = Math.min(100, Math.max(0, value));
  const colour = deriveGaugeColour(clamped, warnThreshold, critThreshold);
  const displayValue = clamped.toFixed(1);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.label}>{label}</span>
        <span className={styles.value}>
          {displayValue}
          {unit}
        </span>
      </div>
      <div
        className={styles.track}
        role="meter"
        aria-label={`${label}: ${displayValue}${unit}`}
        aria-valuenow={clamped}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`${styles.fill} ${styles[colour]}`}
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
